'use client';
// Retours des usagers — guichet unique des avis déposés depuis le site public.
//
// ── Ce que cet écran remplace ───────────────────────────────────────────────
//
// Les avis arrivaient dans la boîte « Demandes reçues » du SITE, comme de
// simples messages de contact, avec leur nature écrite en tête du texte
// (« [Une réclamation] … »). Trois conséquences, toutes constatées :
//   · personne ne pouvait compter — « combien de compliments ce trimestre ? »
//     n'avait pas de réponse, la nature étant noyée dans le corps du message ;
//   · deux écrans voyaient le même retour, donc chacun pouvait croire que
//     l'autre s'en occupait ;
//   · le seul geste offert était « convertir en réclamation », ce qui classait
//     un compliment ou une suggestion à tort dès qu'on voulait le verser au
//     système qualité.
//
// ── Un registre, trois suites, une seule saisie ─────────────────────────────
//
// Le retour vit ICI du début à la fin. La nature est un CHAMP que la RQ corrige
// d'un clic quand le déposant s'est trompé — c'est le cas le plus fréquent, une
// « suggestion » qui est en réalité une réclamation. Ce n'est pas une recopie
// dans un autre registre : c'est une re-qualification. La nature DÉCLARÉE reste
// affichée à côté, parce que l'écart entre les deux dit quelque chose de la
// façon dont les gens nomment ce qu'ils vivent.
//
// Une seule nature ouvre un dossier : la RÉCLAMATION (§8.6 — accusé, enquête,
// fondée ou non, impact sur des résultats rendus, réponse datée). La SUGGESTION
// est versée au registre du personnel, qui existe déjà. L'AVIS POSITIF ne va
// nulle part, et c'est volontaire : il n'a pas besoin d'un dossier, il a besoin
// d'être lu, transmis à l'équipe qu'il nomme, et compté.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, RefISO, Skeleton,
  useBusy, Modale } from '@/components/ui';
import { JournalFiche } from '@/components/JournalFiche';
import { H1, lbl } from './ressources/commun';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
type Ligne = {
  documentId: string; ref: string;
  nature: string; natureDeclaree: string; requalifie: boolean;
  message: string; nom: string; contact: string; anonyme: boolean;
  langue: string; equipe: string; dateDepot: string;
  statut: string; responsable: string;
  reponse: string; repondPar: string; dateReponse: string;
  reclamationRef: string; suggestionRef: string;
  /** Réponses au quiz de satisfaction, figées au dépôt (libellé compris). */
  reponsesQuiz?: { id: string; libelle: string; type: 'note' | 'ouinon'; valeur: number | string }[];
};
type Dossier = {
  lignes: Ligne[];
  natures: { code: string; label: string }[];
  statuts: { code: string; label: string }[];
  peutTraiter: boolean;
  compte: {
    total: number; positifs: number; reclamations: number; suggestions: number;
    aTraiter: number; sansReponse: number; anonymes: number; requalifies: number;
    escalades: number; delaiMoyenJours: number | null;
  };
};

const COULEUR_NATURE: Record<string, { bg: string; fg: string }> = {
  positif: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  reclamation: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  suggestion: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
};
/** Réponses au quiz de satisfaction, telles que posées au dépôt. Affichées
 *  PARTOUT où le retour se lit (fiche et liste) : c'est la seule donnée
 *  structurée que le déposant laisse, la cacher dans la seule fiche la ferait
 *  rater au survol du registre. Le libellé vient du retour lui-même, pas du
 *  paramétrage du jour — une question reformulée ne réécrit pas l'historique. */
function ReponsesQuiz({ reponses, compact }: { reponses: NonNullable<Ligne['reponsesQuiz']>; compact?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: compact ? 12 : 8, flexWrap: 'wrap', flexDirection: compact ? 'row' : 'column' }}>
      {reponses.map((r) => (
        <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: compact ? 11.5 : 12.5, color: 'var(--text-body)' }}>
          <span style={{ color: compact ? 'var(--text-muted)' : 'var(--text-body)' }}>{r.libelle}</span>
          {r.type === 'note' ? (
            <span aria-label={`${r.valeur}/5`} style={{ color: 'var(--amber-500)', letterSpacing: 1 }}>
              {'★'.repeat(Number(r.valeur) || 0)}{'☆'.repeat(Math.max(0, 5 - (Number(r.valeur) || 0)))}
            </span>
          ) : (
            <strong style={{ color: r.valeur === 'oui' ? 'var(--green-600)' : 'var(--red-600)' }}>
              {r.valeur === 'oui' ? 'Oui' : 'Non'}
            </strong>
          )}
        </span>
      ))}
    </div>
  );
}

const COULEUR_STATUT: Record<string, { bg: string; fg: string }> = {
  recu: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  lu: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  repondu: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  clos: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

/** Traitement d'un retour : la nature, la réponse, le statut, et l'escalade. */
function Traitement({ ligne, natures, statuts, onFait, onAnnule }: {
  ligne: Ligne;
  natures: { code: string; label: string }[];
  statuts: { code: string; label: string }[];
  onFait: () => void; onAnnule: () => void;
}) {
  const [nature, setNature] = useState(ligne.nature);
  const [statut, setStatut] = useState(ligne.statut);
  const [reponse, setReponse] = useState(ligne.reponse || '');
  const [equipe, setEquipe] = useState(ligne.equipe || '');
  /* Ce que l'écran a lu, figé à l'ouverture (14/10/2026) : le moteur n'écrit
     que ce qui a changé depuis, et refuse (409) ce qu'un autre a changé. */
  const [base] = useState(() => ({ nature: ligne.nature, statut: ligne.statut, reponse: ligne.reponse || '', equipe: ligne.equipe || '' }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/retours/${ligne.documentId}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nature, statut, reponse, equipe, base }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  /* ⚠️ LA SAISIE PART AVEC L'ESCALADE (07/10/2026).
     Ce bouton referme la fenêtre et recharge la liste. Il n'envoyait que la
     cible : la réponse au déposant et l'équipe tapées juste avant, sans
     « Enregistrer », étaient perdues SANS UN MOT — même défaut qu'au registre
     des suggestions, même jour. On les joint donc à l'appel ; le serveur les
     écrit dans la même mise à jour que la référence produite, et n'efface rien
     si elles sont vides.

     `nature` et `statut` ne partent PAS : l'escalade les décide (la cible donne
     la nature, le retour passe « lu »). Ce que la fenêtre en montre est donc
     écarté — et c'est dit sous les boutons, au lieu de se faire en silence. */
  const escalader = (cible: 'reclamation' | 'suggestion') => run(cible, async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/retours/${ligne.documentId}/escalade`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cible, reponse, equipe }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Opération refusée.');
      return;
    }
    onFait();
  });


  return (
    <Modale titre={`${ligne.ref} — traiter le retour`} onClose={onAnnule} largeur={760}>
      <div style={{ display: 'grid', gap: 14 }}>
        {/* Le texte du déposant en premier, tel qu'il l'a écrit. Le reste est
            du travail sur ce texte : il ne doit pas passer après les champs. */}
        <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: 13, lineHeight: 1.55, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>
          {ligne.message}
        </div>

        {/* Le quiz sous le message : c'est aussi une parole du déposant. */}
        {!!ligne.reponsesQuiz?.length && (
          <div style={{ background: 'var(--cyan-50)', borderRadius: 'var(--radius-md)', padding: '10px 14px' }}>
            <div style={lbl}>Quiz de satisfaction</div>
            <ReponsesQuiz reponses={ligne.reponsesQuiz} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-muted)' }}>
          <span>{ligne.anonyme ? 'Dépôt anonyme' : ligne.nom || 'Sans nom'}</span>
          {ligne.contact && <span style={{ fontFamily: 'var(--font-mono)', direction: 'ltr' }}>{ligne.contact}</span>}
          <span>{fmtDate(ligne.dateDepot)}</span>
          {ligne.langue && ligne.langue !== 'fr' && <span>déposé en « {ligne.langue} »</span>}
        </div>

        <div>
          <div style={lbl}>Nature retenue</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Segment ariaLabel="Nature" valeur={nature} onChange={setNature} options={natures.map((n) => ({ valeur: n.code, libelle: n.label }))} />
          </div>
          {/* L'écart entre déclaré et retenu est montré, jamais effacé : il dit
              comment les gens nomment ce qu'ils vivent, et c'est une donnée. */}
          {ligne.natureDeclaree && ligne.natureDeclaree !== nature && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>
              Le déposant avait choisi « {natures.find((n) => n.code === ligne.natureDeclaree)?.label || ligne.natureDeclaree} ».
            </div>
          )}
        </div>

        {/* L'équipe citée n'a de sens que pour un compliment : c'est le seul cas
            où quelque chose doit être TRANSMIS plutôt que traité. */}
        {nature === 'positif' && (
          <div>
            <div style={lbl}>Équipe ou personne à qui transmettre</div>
            <input value={equipe} onChange={(e) => setEquipe(e.target.value)} style={inputStyle}
              placeholder="Accueil, prélèvement, secrétariat…" />
          </div>
        )}

        <div>
          <div style={lbl}>Réponse au déposant</div>
          <textarea value={reponse} onChange={(e) => setReponse(e.target.value)} rows={4}
            style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 }}
            placeholder={ligne.contact ? 'Ce qui sera écrit au déposant.' : 'Aucune adresse n’a été laissée : cette réponse restera interne.'} />
          {!ligne.contact && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
              Dépôt sans coordonnées — il n’y a personne à qui répondre. Ce champ garde la trace de ce qui a été décidé.
            </div>
          )}
        </div>

        <div>
          <div style={lbl}>Statut</div>
          <select value={statut} onChange={(e) => setStatut(e.target.value)} style={inputStyle}>
            {statuts.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </div>

        {/* L'escalade reste un acte SÉPARÉ de l'enregistrement : elle crée un
            enregistrement ailleurs, et c'est elle qui décide la nature.
            ⚠️ Elle emporte néanmoins la réponse et l'équipe saisies. Le
            commentaire qui tenait ici disait qu'un tel geste « ne doit jamais
            partir en même temps qu'une saisie de texte » : l'intention était de
            ne pas mêler les gestes, mais l'effet était de DÉTRUIRE le premier
            en faisant le second, puisque la fenêtre se referme ensuite. */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'grid', gap: 8 }}>
          <div style={lbl}>Porter au registre qui convient</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {ligne.reclamationRef ? (
              <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                Réclamation ouverte : <strong style={{ fontFamily: 'var(--font-mono)' }}>{ligne.reclamationRef}</strong>
              </span>
            ) : (
              <Bouton taille="sm" onClick={() => escalader('reclamation')} disabled={estOccupe()}>
                <I n="alert-triangle" s={14} />Ouvrir une réclamation
              </Bouton>
            )}
            {ligne.suggestionRef ? (
              <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                Suggestion versée : <strong style={{ fontFamily: 'var(--font-mono)' }}>{ligne.suggestionRef}</strong>
              </span>
            ) : (
              <Bouton taille="sm" onClick={() => escalader('suggestion')} disabled={estOccupe()}>
                <I n="bulb" s={14} />Verser aux suggestions
              </Bouton>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Un avis positif n’ouvre aucun dossier : il est lu, transmis, et compté. Seule la
            réclamation entre dans le circuit <RefISO code="8.6" /> avec ses délais.
            {' '}Porter le retour enregistre la <strong>réponse</strong> et l’<strong>équipe</strong> saisies
            ci-dessus ; la <strong>nature</strong> devient celle du registre choisi et le statut passe
            à « lu ».
          </div>
        </div>

        {erreur && (
          <div style={{ background: 'var(--red-50)', color: 'var(--red-600)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', fontSize: 12.5, fontWeight: 600 }}>{erreur}</div>
        )}

        <PiedActions annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe(), onClick: enregistrer }} />

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
          <JournalFiche code={ligne.ref} compact />
        </div>
      </div>
    </Modale>
  );
}

export function Retours() {
  const { settings } = useGed();
  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [ouverte, setOuverte] = useState<Ligne | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/retours', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setOuverte(null); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => (
      filtre === 'attente' ? ['recu', 'lu'].includes(l.statut)
        : filtre === 'sansreponse' ? !String(l.reponse || '').trim() && !!l.contact
          : ['positif', 'reclamation', 'suggestion'].includes(filtre) ? l.nature === filtre
            : true));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.ref} ${l.message} ${l.nom} ${l.equipe}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q]);

  /* ⚠️ ON GARDE LA COQUILLE. Cet écran remplaçait TOUT — titre, chapeau,
     filtres — par la ligne « Chargement des retours… », si bien que la page
     semblait changer de nature à chaque visite. Le titre et le chapeau ne
     dépendent d'aucune donnée : rien ne justifiait de les démonter, et les
     laisser en place évite le saut de mise en page à l'arrivée des lignes.
     Le squelette dit « ça vient » là où les lignes se poseront — jamais un
     « aucun retour » qu'on ne sait pas encore vrai (relevé BLOQ-03). */
  if (chargement) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <H1 eyebrow={<>Amélioration · <RefISO code="8.6" /></>} title="Retours des usagers" />
        <div style={card}><Skeleton rows={4} height={52} /></div>
      </div>
    );
  }
  if (!d) return null;
  const { compte, peutTraiter, natures } = d;
  const labelNature = (code: string) => natures.find((n) => n.code === code)?.label || code;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Amélioration · <RefISO code="8.6" /></>} title="Retours des usagers" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Ce que les patients et les confrères écrivent depuis le site, par le bouton
        <strong> « Votre avis »</strong>. Chaque retour vit ici du début à la fin : la nature se
        corrige d’un clic si le déposant s’est trompé. Seule la <strong>réclamation</strong> ouvre
        un dossier avec ses délais ; la suggestion part au registre du personnel ; l’avis positif
        est lu, transmis et compté.
      </p>

      {compte.sansReponse > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong>{compte.sansReponse} retour{compte.sansReponse > 1 ? 's' : ''} sans réponse</strong> alors
          qu’une adresse a été laissée. C’est le seul chiffre qui dit si le canal vit — les dépôts
          anonymes n’y comptent pas, on ne leur doit rien qu’on puisse leur envoyer.
        </div>
      )}

      {compte.total === 0 && (
        <EmptyState
          titre="Aucun retour n’a encore été déposé."
          indice="Le guichet est ouvert : le bouton « Votre avis » du site permet d’écrire en un champ, sans coordonnées obligatoires sauf pour une réclamation. Un registre vide n’est pas un défaut — c’est un registre dont il reste à vérifier que le bouton est visible." />
      )}

      {compte.total > 0 && (
        <>
          {/* Les chiffres que la revue de direction vient chercher, à l'endroit
              où ils se lisent — pas dans un rapport à reconstruire. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
            {[
              { l: 'Avis positifs', v: String(compte.positifs) },
              { l: 'Réclamations', v: String(compte.reclamations) },
              { l: 'Suggestions', v: String(compte.suggestions) },
              { l: 'Délai moyen de réponse', v: compte.delaiMoyenJours === null ? '—' : `${compte.delaiMoyenJours} j` },
              { l: 'Re-qualifiés', v: String(compte.requalifies) },
            ].map((c) => (
              <div key={c.l} style={{ ...card, padding: '11px 13px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 700, color: 'var(--text-strong)' }}>{c.v}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{c.l}</div>
              </div>
            ))}
          </div>

          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un retour…"
            chips={[
              { k: 'tous', label: 'Tous', n: compte.total },
              { k: 'attente', label: 'À traiter', n: compte.aTraiter },
              { k: 'sansreponse', label: 'Sans réponse', n: compte.sansReponse },
              { k: 'positif', label: 'Avis positifs', n: compte.positifs },
              { k: 'reclamation', label: 'Réclamations', n: compte.reclamations },
              { k: 'suggestion', label: 'Suggestions', n: compte.suggestions },
            ]}
            actif={filtre} onChip={setFiltre}
            total={compte.total} affiches={filtrees.length}
            onReset={() => { setQ(''); setFiltre('tous'); }} />

          {ouverte && peutTraiter && (
            <Traitement ligne={ouverte} natures={natures} statuts={d.statuts} onFait={recharger} onAnnule={() => setOuverte(null)} />
          )}

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtrees.map((l, i) => {
              const cn = COULEUR_NATURE[l.nature] || COULEUR_NATURE.positif;
              const cs = COULEUR_STATUT[l.statut] || COULEUR_STATUT.recu;
              return (
                /* Toute la ligne ouvre la fiche, comme au registre des
                   suggestions. Rendue comme un <button> : le clavier et les
                   lecteurs d'écran la trouvent. */
                <button
                  key={l.documentId}
                  type="button"
                  onClick={() => { if (peutTraiter) setOuverte(l); }}
                  disabled={!peutTraiter}
                  className={peutTraiter ? 'ged-row' : undefined}
                  aria-label={`Ouvrir ${l.ref}`}
                  style={{ display: 'block', width: '100%', textAlign: 'start', font: 'inherit', fontFamily: 'var(--font-sans)', background: 'none', border: 'none', borderTop: i ? '1px solid var(--border-subtle)' : 'none', padding: '11px 14px', cursor: peutTraiter ? 'pointer' : 'default' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ref}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: cn.bg, color: cn.fg }}>{labelNature(l.nature)}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: cs.bg, color: cs.fg }}>
                      {d.statuts.find((s) => s.code === l.statut)?.label || l.statut}
                    </span>
                    {l.requalifie && (
                      <span title={`Le déposant avait choisi « ${labelNature(l.natureDeclaree)} »`}
                        style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>re-qualifié</span>
                    )}
                    {l.reclamationRef && (
                      <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--red-600)' }}>→ {l.reclamationRef}</span>
                    )}
                    {l.suggestionRef && (
                      <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--cyan-700)' }}>→ {l.suggestionRef}</span>
                    )}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {l.anonyme ? 'dépôt anonyme' : l.nom || 'sans nom'} · {fmtDate(l.dateDepot)}
                    </span>
                  </div>

                  <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{l.message}</div>

                  {!!l.reponsesQuiz?.length && (
                    <div style={{ marginTop: 5 }}>
                      <ReponsesQuiz reponses={l.reponsesQuiz} compact />
                    </div>
                  )}

                  {l.reponse ? (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 6, lineHeight: 1.5, borderInlineStart: '2px solid var(--green-600)', paddingInlineStart: 9 }}>
                      <strong style={{ color: 'var(--text-strong)' }}>Réponse</strong>
                      {l.repondPar ? ` — ${userName(settings, l.repondPar)}` : ''}
                      {l.dateReponse ? `, ${fmtDate(l.dateReponse)}` : ''} : {l.reponse}
                    </div>
                  ) : l.contact ? (
                    <div style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700, marginTop: 5 }}>
                      Pas encore de réponse — une adresse a été laissée.
                    </div>
                  ) : null}

                  <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 12.5, color: 'var(--text-muted)', flexWrap: 'wrap', alignItems: 'center' }}>
                    {l.equipe && <span>transmis à « {l.equipe} »</span>}
                    <span style={{ flex: 1 }} />
                    {peutTraiter && (
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
                        {l.reponse ? 'Ouvrir' : 'Traiter'} →
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucun retour ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
